import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import { useController, UseFormReturn } from 'react-hook-form';
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from 'react-google-places-autocomplete';
import { Address } from '@website/types/graphqlOperations';
import useScript from '../../../hooks/useScript';
import LoadingPage from '../../utilities/LoadingPage';
import config from '../../../config';
import ErrorPage from '../../utilities/ErrorPage';
import GoogleMapsAutocompleteFieldAddressView from './utils/GoogleMapsAutocompleteFieldAddressView';
import googleAddressToStandard from './utils/googleAddressToStandard';

type Props = {
  name: string;
  control: UseFormReturn<any>['control'];
  required?: boolean;
  /**
   * setError
   *
   * React Hook Form setError Method
   *
   * For more information, please check out {@link https://react-hook-form.com/api/useform/seterror/}
   *
   */
  setError: UseFormReturn<any>['setError'];
  clearErrors: UseFormReturn<any>['clearErrors'];
};

/**
 *
 * A Google Maps Autocomplete Field with a Map to Indicate Selected Location
 *
 * NB: Testing the GooglePlacesAutocomplete is not Easy as they don't use native
 * input elements and it's hard to target the element to type in without resulting
 * to walking through th dom elements one by one. You can mock out this component
 * to set the address [LegacyAddress] object or mock out the GooglePlacesAutocomplete
 * component it self.
 *
 */
export default function GoogleMapsAutocompleteField({
  control,
  name,
  required = false,
  setError,
  clearErrors,
}: Props) {
  // Setup the form
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<{ [name: string]: Address }>({
    name,
    control,
    rules: { required },
  });

  // Load google script
  const [loaded, scriptLoadingError] = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${config.Google.placesApiKey}&libraries=places`,
  );

  // Add in the map
  const [map, setMap] = useState<google.maps.Map>();
  const mapRef = useRef(null);
  useEffect(() => {
    if (loaded && mapRef.current) {
      // Setup the map
      const mapSetup = new window.google.maps.Map(mapRef.current, {
        // Default center to everest
        center: {
          lat: value?.coordinates.lat || 27.988,
          lng: value?.coordinates.lng || 86.925,
        },
        disableDefaultUI: true,
        minZoom: 14, // Don't allow zooming out too far, so the marker can't be dragged too far from the address
        zoom: 15,
      });

      setMap(mapSetup);
    }
    // Don't update the map for each lat/lng change (as it causes too many re-renders with dragging), just if the address
    // itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loaded,
    value?.addressLine1,
    value?.addressLine2,
    value?.addressLine2,
    value?.administrativeArea,
    value?.locality,
    value?.postalCode,
  ]);

  // Add in the marker
  // Handle loaded maps api
  useEffect(() => {
    if (loaded && map && value?.coordinates.lat && value?.coordinates.lng) {
      const marker = new window.google.maps.Marker({
        draggable: true,
        map,
        position: {
          lat: value.coordinates.lat,
          lng: value.coordinates.lng,
        },
      });

      // Change the latitude and longitude on drag
      marker.addListener('drag', (dragEvent) => {
        const newValue = {
          ...value,
          coordinates: {
            lat: dragEvent.latLng.lat(),
            lng: dragEvent.latLng.lng(),
          },
        };
        onChange(newValue);
      });
    }

    // Similarly to the map hook, don't trigger a re-render on every drag movement
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loaded,
    map,
    onChange,
    value?.addressLine1,
    value?.addressLine2,
    value?.addressLine2,
    value?.administrativeArea,
    value?.locality,
    value?.postalCode,
  ]);

  // handle script loading and error
  if (!loaded) return <LoadingPage />;
  if (scriptLoadingError) return <ErrorPage />;

  return (
    <>
      <GooglePlacesAutocomplete
        apiKey={config.Google.placesApiKey}
        selectProps={{
          placeholder: 'Search',
          onChange: async (event: GoogleMapsAutocompleteEvent) => {
            // If there are no results, return without setting anything
            if (!event?.value) return;

            // Get the place name and ID
            const placeName = event.value.structured_formatting.main_text;
            const id = event.value.place_id;

            // Geocode
            const res = await geocodeByPlaceId(id);

            // Format the address
            const address = googleAddressToStandard(res[0], placeName);

            // Set error if there is no address country (e.g. we've just set as Europe)
            if (!address.country) {
              setError(name, {
                message:
                  'The address area is too big - try putting somewhere more specific (e.g. a street address)',
              });
              return;
            }

            // Clean up errors otherwise, from previous changes
            clearErrors(name);

            // Set the value
            onChange(address);
          },
        }}
        withSessionToken
      />
      <Row style={{ alignItems: 'stretch', margin: '5px 0 10px' }}>
        <Col
          xs={value ? 6 : 12}
          sm={value ? 8 : 12}
          lg={value ? 9 : 12}
          ref={mapRef}
          style={{
            minHeight: 200,
            borderColor: '#ced4da',
            borderStyle: 'solid',
            borderWidth: 1,
            borderRadius: 1,
          }}
        />
        {value && (
          <Col
            xs={6}
            sm={4}
            lg={3}
            style={{
              background: '#e9ecef',
              borderColor: '#ced4da',
              borderStyle: 'solid',
              borderWidth: 1,
              borderLeftWidth: 0,
              borderRadius: 1,
              paddingTop: 5,
              paddingBottom: 5,
            }}
          >
            <GoogleMapsAutocompleteFieldAddressView address={value} />
          </Col>
        )}
      </Row>
      {error && error.message && (
        <Alert variant="danger">{error.message}</Alert>
      )}
    </>
  );
}

/**
 * Google Maps auto complete on change event
 */
export interface GoogleMapsAutocompleteEvent {
  /** Long text */
  label: string;

  /** Value is only given if there is a hit */
  value?: {
    /** Description is generally the same as label */
    description: string;

    /** Google Maps place ID */
    place_id?: string;

    /** Reference appears to be the same as place ID */
    reference?: string;

    structured_formatting: {
      /** The place title (e.g. "Sainsbury") */
      main_text: string;

      /** The rest of the address (e.g. street, city, country) */
      secondary_text?: string;
    };

    /** Terms appears to be the address split by commas */
    terms: {
      /** String offset */
      offset: number;

      /** String value */
      value: string;
    }[];

    types?: PlaceType[];
  };
}

enum PlaceType {
  RESTAURANT = 'restaurant',
  FOOD = 'food',
  POINT_OF_INTEREST = 'point_of_interest',
  ESTABLISHMENT = 'establishment',
  PREMISE = 'premise',
}
